import { useState, useEffect, useCallback } from 'react'
import type { Camera, CameraCapabilities, Point, MotionDetectionConfig, PerimeterConfig, TripWireConfig, AreaDetectionConfig, PeopleCountingConfig, PrivacyMask } from '../../types'
import { IntelligenceType } from '../../types'
import { useToast } from '../ui/Toast'
import { supabase } from '../../lib/supabase'
import { ZoneEditor } from './ZoneEditor'
import {
  probeCapabilities,
  getMotionDetection, setMotionDetection,
  getPerimeter, setPerimeter,
  getTripWire, setTripWire,
  getAreaDetection, setAreaDetection,
  getPeopleCounting, setPeopleCounting,
  getPrivacyMasks, setPrivacyMask, deletePrivacyMask,
} from '../../lib/cameraApi'

interface Props {
  camera: Camera
  onClose: () => void
}

type Tab = 'motion' | 'perimeter' | 'tripwire' | 'enterArea' | 'leaveArea' | 'counting' | 'privacy'

const TAB_LABELS: Record<Tab, string> = {
  motion: 'Motion',
  perimeter: 'Intrusion',
  tripwire: 'Line Cross',
  enterArea: 'Enter Area',
  leaveArea: 'Leave Area',
  counting: 'People Count',
  privacy: 'Privacy Mask',
}

export function CameraSettingsPanel({ camera, onClose }: Props) {
  const { toast } = useToast()
  const [caps, setCaps] = useState<CameraCapabilities | null>(null)
  const [tab, setTab] = useState<Tab>('motion')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [authToken, setAuthToken] = useState('')

  // Detection configs
  const [motion, setMotion] = useState<MotionDetectionConfig | null>(null)
  const [perimeterCfg, setPerimeterCfg] = useState<PerimeterConfig | null>(null)
  const [tripWireCfg, setTripWireCfg] = useState<TripWireConfig | null>(null)
  const [enterAreaCfg, setEnterAreaCfg] = useState<AreaDetectionConfig | null>(null)
  const [leaveAreaCfg, setLeaveAreaCfg] = useState<AreaDetectionConfig | null>(null)
  const [countingCfg, setCountingCfg] = useState<PeopleCountingConfig | null>(null)
  const [masks, setMasks] = useState<PrivacyMask[]>([])

  // Get auth token for snapshot
  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) setAuthToken(data.session.access_token)
    })
  }, [])

  // Probe capabilities + load current tab config
  useEffect(() => {
    setLoading(true)
    probeCapabilities(camera.id).then(c => {
      setCaps(c)
      // Default to first available tab
      if (c.intelligences.includes(IntelligenceType.Perimeter)) setTab('perimeter')
      else setTab('motion')
      setLoading(false)
    }).catch(() => {
      setCaps({ ptz: true, presetMaxNum: 16, intelligences: [] })
      setLoading(false)
    })
  }, [camera.id])

  // Load config when tab changes
  useEffect(() => {
    if (!caps) return
    const load = async () => {
      setLoading(true)
      try {
        if (tab === 'motion') setMotion(await getMotionDetection(camera.id))
        else if (tab === 'perimeter') setPerimeterCfg(await getPerimeter(camera.id))
        else if (tab === 'tripwire') setTripWireCfg(await getTripWire(camera.id))
        else if (tab === 'enterArea') setEnterAreaCfg(await getAreaDetection(camera.id, 'enterArea'))
        else if (tab === 'leaveArea') setLeaveAreaCfg(await getAreaDetection(camera.id, 'leaveArea'))
        else if (tab === 'counting') setCountingCfg(await getPeopleCounting(camera.id))
        else if (tab === 'privacy') setMasks(await getPrivacyMasks(camera.id))
      } catch {
        toast('Failed to load settings', 'error')
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [tab, caps, camera.id, toast])

  // Available tabs based on capabilities
  const availableTabs: Tab[] = ['motion'] // motion always available
  if (caps) {
    if (caps.intelligences.includes(IntelligenceType.Perimeter)) availableTabs.push('perimeter')
    if (caps.intelligences.includes(IntelligenceType.TripWire)) availableTabs.push('tripwire')
    if (caps.intelligences.includes(IntelligenceType.EnterArea)) availableTabs.push('enterArea')
    if (caps.intelligences.includes(IntelligenceType.LeaveArea)) availableTabs.push('leaveArea')
    if (caps.intelligences.includes(IntelligenceType.PeopleCounting)) availableTabs.push('counting')
  }
  availableTabs.push('privacy') // privacy mask always available

  // ── Save handlers ──

  const saveMotion = useCallback(async () => {
    if (!motion) return
    setSaving(true)
    try {
      await setMotionDetection(camera.id, motion)
      toast('Motion detection saved', 'success')
    } catch { toast('Save failed', 'error') }
    finally { setSaving(false) }
  }, [camera.id, motion, toast])

  const savePerimeter = useCallback(async () => {
    if (!perimeterCfg) return
    setSaving(true)
    try {
      await setPerimeter(camera.id, perimeterCfg)
      toast('Intrusion detection saved', 'success')
    } catch { toast('Save failed', 'error') }
    finally { setSaving(false) }
  }, [camera.id, perimeterCfg, toast])

  const saveTripWire = useCallback(async () => {
    if (!tripWireCfg) return
    setSaving(true)
    try {
      await setTripWire(camera.id, tripWireCfg)
      toast('Line crossing saved', 'success')
    } catch { toast('Save failed', 'error') }
    finally { setSaving(false) }
  }, [camera.id, tripWireCfg, toast])

  const saveAreaDetection = useCallback(async (type: 'enterArea' | 'leaveArea') => {
    const cfg = type === 'enterArea' ? enterAreaCfg : leaveAreaCfg
    if (!cfg) return
    setSaving(true)
    try {
      await setAreaDetection(camera.id, type, cfg)
      toast(`${type === 'enterArea' ? 'Enter' : 'Leave'} area saved`, 'success')
    } catch { toast('Save failed', 'error') }
    finally { setSaving(false) }
  }, [camera.id, enterAreaCfg, leaveAreaCfg, toast])

  const saveCounting = useCallback(async () => {
    if (!countingCfg) return
    setSaving(true)
    try {
      await setPeopleCounting(camera.id, countingCfg)
      toast('People counting saved', 'success')
    } catch { toast('Save failed', 'error') }
    finally { setSaving(false) }
  }, [camera.id, countingCfg, toast])

  // ── Styles ──
  const btnSm = 'text-xs px-2.5 py-1.5 rounded transition-colors font-medium'
  const btnSave = `${btnSm} bg-brand hover:bg-brand-dark text-white disabled:opacity-50`
  const toggleCls = (on: boolean) => `relative w-9 h-5 rounded-full transition-colors cursor-pointer ${on ? 'bg-brand' : 'bg-gray-300'}`
  const toggleDot = (on: boolean) => `absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${on ? 'translate-x-4' : 'translate-x-0.5'}`

  return (
    <div className="fixed inset-0 z-20 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-2xl w-[800px] max-w-[95vw] max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
          <div>
            <h2 className="text-sm font-semibold text-gray-900">{camera.name} — Settings</h2>
            {caps?.deviceModel && <p className="text-[10px] text-gray-500">{caps.deviceModel} {caps.firmwareVersion ? `· FW ${caps.firmwareVersion}` : ''}</p>}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-lg transition-colors">&times;</button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 px-5 gap-1 overflow-x-auto">
          {availableTabs.map(t => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`text-xs font-medium px-3 py-2.5 border-b-2 transition-colors whitespace-nowrap ${
                tab === t
                  ? 'border-brand text-brand'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {TAB_LABELS[t]}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="text-center text-sm text-gray-500 py-8">Loading...</div>
          ) : (
            <>
              {/* Motion Detection */}
              {tab === 'motion' && motion && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-gray-900">Motion Detection</label>
                    <div className={toggleCls(motion.enabled)} onClick={() => setMotion({ ...motion, enabled: !motion.enabled })}>
                      <div className={toggleDot(motion.enabled)} />
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <label className="text-xs text-gray-600 w-20">Sensitivity</label>
                    <input type="range" min={1} max={10} value={motion.sensitivity} onChange={e => setMotion({ ...motion, sensitivity: Number(e.target.value) })} className="flex-1 accent-brand" />
                    <span className="text-xs text-gray-600 w-6 text-right">{motion.sensitivity}</span>
                  </div>
                  <ZoneEditor
                    cameraId={camera.id}
                    authToken={authToken}
                    mode="rect"
                    rects={motion.zones.map((z, i) => ({
                      id: i,
                      x: (z.topX / 420) * 100,
                      y: (z.topY / 260) * 100,
                      w: (z.width / 420) * 100,
                      h: (z.height / 260) * 100,
                      color: 'rgba(234, 179, 8, 0.6)',
                    }))}
                    onRectComplete={(x, y, w, h) => {
                      const zone = {
                        topX: Math.round((x / 100) * 420),
                        topY: Math.round((y / 100) * 260),
                        width: Math.round((w / 100) * 420),
                        height: Math.round((h / 100) * 260),
                      }
                      setMotion({ ...motion, zones: [...motion.zones, zone] })
                    }}
                    onDelete={id => setMotion({ ...motion, zones: motion.zones.filter((_, i) => i !== id) })}
                  />
                  <button onClick={saveMotion} disabled={saving} className={btnSave}>
                    {saving ? 'Saving...' : 'Save Motion Detection'}
                  </button>
                </div>
              )}

              {/* Perimeter / Intrusion */}
              {tab === 'perimeter' && perimeterCfg && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-gray-900">Intrusion Detection</label>
                    <div className={toggleCls(perimeterCfg.enabled)} onClick={() => setPerimeterCfg({ ...perimeterCfg, enabled: !perimeterCfg.enabled })}>
                      <div className={toggleDot(perimeterCfg.enabled)} />
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="flex items-center gap-2">
                      <label className="text-xs text-gray-600">Sensitivity</label>
                      <select value={perimeterCfg.sensitivity} onChange={e => setPerimeterCfg({ ...perimeterCfg, sensitivity: Number(e.target.value) })} className="text-xs border rounded px-2 py-1">
                        {[1, 2, 3, 4, 5].map(v => <option key={v} value={v}>{v}</option>)}
                      </select>
                    </div>
                    <div className="flex items-center gap-2">
                      <label className="text-xs text-gray-600">Target</label>
                      <select value={perimeterCfg.targetType} onChange={e => setPerimeterCfg({ ...perimeterCfg, targetType: Number(e.target.value) })} className="text-xs border rounded px-2 py-1">
                        <option value={0}>Person + Vehicle</option>
                        <option value={1}>Person Only</option>
                        <option value={2}>Vehicle Only</option>
                      </select>
                    </div>
                  </div>
                  <p className="text-[10px] text-gray-500">Draw polygons to define intrusion zones. Click points to form a shape, click near the first point to close.</p>
                  <ZoneEditor
                    cameraId={camera.id}
                    authToken={authToken}
                    mode="polygon"
                    polygons={perimeterCfg.regions.map(r => ({
                      id: r.id,
                      points: r.points,
                      color: 'rgba(220, 38, 38, 0.3)',
                    }))}
                    onPolygonComplete={(points: Point[]) => {
                      const newRegion = { id: perimeterCfg.regions.length, enabled: true, points }
                      setPerimeterCfg({ ...perimeterCfg, regions: [...perimeterCfg.regions, newRegion] })
                    }}
                    onDelete={id => setPerimeterCfg({ ...perimeterCfg, regions: perimeterCfg.regions.filter(r => r.id !== id) })}
                  />
                  <button onClick={savePerimeter} disabled={saving} className={btnSave}>
                    {saving ? 'Saving...' : 'Save Intrusion Detection'}
                  </button>
                </div>
              )}

              {/* TripWire / Line Crossing */}
              {tab === 'tripwire' && tripWireCfg && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-gray-900">Line Crossing Detection</label>
                    <div className={toggleCls(tripWireCfg.enabled)} onClick={() => setTripWireCfg({ ...tripWireCfg, enabled: !tripWireCfg.enabled })}>
                      <div className={toggleDot(tripWireCfg.enabled)} />
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-gray-600">Target</label>
                    <select value={tripWireCfg.targetType} onChange={e => setTripWireCfg({ ...tripWireCfg, targetType: Number(e.target.value) })} className="text-xs border rounded px-2 py-1">
                      <option value={0}>Person + Vehicle</option>
                      <option value={1}>Person Only</option>
                      <option value={2}>Vehicle Only</option>
                    </select>
                  </div>
                  <p className="text-[10px] text-gray-500">Draw trip wire lines. Click start point, then click end point. Arrow shows detection direction.</p>
                  <ZoneEditor
                    cameraId={camera.id}
                    authToken={authToken}
                    mode="line"
                    lines={tripWireCfg.lines.map(l => ({
                      id: l.id,
                      startX: l.startX,
                      startY: l.startY,
                      endX: l.endX,
                      endY: l.endY,
                      color: 'rgba(59, 130, 246, 0.9)',
                    }))}
                    onLineComplete={(start, end) => {
                      const newLine = { id: tripWireCfg.lines.length, startX: start.x, startY: start.y, endX: end.x, endY: end.y }
                      setTripWireCfg({ ...tripWireCfg, lines: [...tripWireCfg.lines, newLine] })
                    }}
                    onDelete={id => setTripWireCfg({ ...tripWireCfg, lines: tripWireCfg.lines.filter(l => l.id !== id) })}
                  />
                  <button onClick={saveTripWire} disabled={saving} className={btnSave}>
                    {saving ? 'Saving...' : 'Save Line Crossing'}
                  </button>
                </div>
              )}

              {/* Enter Area */}
              {tab === 'enterArea' && enterAreaCfg && (
                <AreaTab
                  label="Enter Area Detection"
                  config={enterAreaCfg}
                  setConfig={setEnterAreaCfg}
                  camera={camera}
                  authToken={authToken}
                  saving={saving}
                  onSave={() => saveAreaDetection('enterArea')}
                  toggleCls={toggleCls}
                  toggleDot={toggleDot}
                  btnSave={btnSave}
                />
              )}

              {/* Leave Area */}
              {tab === 'leaveArea' && leaveAreaCfg && (
                <AreaTab
                  label="Leave Area Detection"
                  config={leaveAreaCfg}
                  setConfig={setLeaveAreaCfg}
                  camera={camera}
                  authToken={authToken}
                  saving={saving}
                  onSave={() => saveAreaDetection('leaveArea')}
                  toggleCls={toggleCls}
                  toggleDot={toggleDot}
                  btnSave={btnSave}
                />
              )}

              {/* People Counting */}
              {tab === 'counting' && countingCfg && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-gray-900">People Counting</label>
                    <div className={toggleCls(countingCfg.enabled)} onClick={() => setCountingCfg({ ...countingCfg, enabled: !countingCfg.enabled })}>
                      <div className={toggleDot(countingCfg.enabled)} />
                    </div>
                  </div>
                  <div className="flex gap-4 flex-wrap">
                    <label className="flex items-center gap-2 text-xs text-gray-600">
                      <input type="checkbox" checked={countingCfg.osdEnabled} onChange={e => setCountingCfg({ ...countingCfg, osdEnabled: e.target.checked })} className="accent-brand" />
                      Show count on video
                    </label>
                    <label className="flex items-center gap-2 text-xs text-gray-600">
                      <input type="checkbox" checked={countingCfg.alarmEnabled} onChange={e => setCountingCfg({ ...countingCfg, alarmEnabled: e.target.checked })} className="accent-brand" />
                      Overcrowding alarm
                    </label>
                  </div>
                  {countingCfg.alarmEnabled && (
                    <div className="flex items-center gap-2">
                      <label className="text-xs text-gray-600">Threshold</label>
                      <input type="number" min={1} max={999} value={countingCfg.alarmThreshold} onChange={e => setCountingCfg({ ...countingCfg, alarmThreshold: Number(e.target.value) })} className="text-xs border rounded px-2 py-1 w-20" />
                    </div>
                  )}
                  <p className="text-[10px] text-gray-500">Draw a counting line. People crossing from A to B direction are counted as outbound.</p>
                  <ZoneEditor
                    cameraId={camera.id}
                    authToken={authToken}
                    mode="line"
                    lines={countingCfg.line ? [{
                      id: 0,
                      startX: countingCfg.line.startX,
                      startY: countingCfg.line.startY,
                      endX: countingCfg.line.endX,
                      endY: countingCfg.line.endY,
                      color: 'rgba(16, 185, 129, 0.9)',
                    }] : []}
                    onLineComplete={(start, end) => {
                      setCountingCfg({ ...countingCfg, line: { startX: start.x, startY: start.y, endX: end.x, endY: end.y } })
                    }}
                    onDelete={() => setCountingCfg({ ...countingCfg, line: undefined })}
                  />
                  <button onClick={saveCounting} disabled={saving} className={btnSave}>
                    {saving ? 'Saving...' : 'Save People Counting'}
                  </button>
                </div>
              )}

              {/* Privacy Mask */}
              {tab === 'privacy' && (
                <div className="space-y-4">
                  <label className="text-sm font-medium text-gray-900 block">Privacy Masks</label>
                  <p className="text-[10px] text-gray-500">Draw rectangles to mask areas. Coordinates are percentages of the video frame.</p>
                  <ZoneEditor
                    cameraId={camera.id}
                    authToken={authToken}
                    mode="rect"
                    rects={masks.map(m => ({
                      id: m.areaId,
                      x: m.topX,
                      y: m.topY,
                      w: m.width,
                      h: m.height,
                      color: 'rgba(0, 0, 0, 0.7)',
                    }))}
                    onRectComplete={async (x, y, w, h) => {
                      const newMask: PrivacyMask = {
                        areaId: masks.length + 1,
                        enabled: true,
                        topX: Math.round(x),
                        topY: Math.round(y),
                        width: Math.round(w),
                        height: Math.round(h),
                        blindType: 1,
                        blindColor: '000000',
                      }
                      setSaving(true)
                      try {
                        await setPrivacyMask(camera.id, newMask)
                        setMasks([...masks, newMask])
                        toast('Privacy mask added', 'success')
                      } catch { toast('Failed to add mask', 'error') }
                      finally { setSaving(false) }
                    }}
                    onDelete={async (id) => {
                      setSaving(true)
                      try {
                        await deletePrivacyMask(camera.id, id)
                        setMasks(masks.filter(m => m.areaId !== id))
                        toast('Privacy mask deleted', 'success')
                      } catch { toast('Failed to delete mask', 'error') }
                      finally { setSaving(false) }
                    }}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

// ── Shared Area Detection Tab ──

function AreaTab({ label, config, setConfig, camera, authToken, saving, onSave, toggleCls, toggleDot, btnSave }: {
  label: string
  config: AreaDetectionConfig
  setConfig: (c: AreaDetectionConfig) => void
  camera: Camera
  authToken: string
  saving: boolean
  onSave: () => void
  toggleCls: (on: boolean) => string
  toggleDot: (on: boolean) => string
  btnSave: string
}) {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-gray-900">{label}</label>
        <div className={toggleCls(config.enabled)} onClick={() => setConfig({ ...config, enabled: !config.enabled })}>
          <div className={toggleDot(config.enabled)} />
        </div>
      </div>
      <div className="flex items-center gap-2">
        <label className="text-xs text-gray-600">Target</label>
        <select value={config.targetType} onChange={e => setConfig({ ...config, targetType: Number(e.target.value) })} className="text-xs border rounded px-2 py-1">
          <option value={0}>Person + Vehicle</option>
          <option value={1}>Person Only</option>
          <option value={2}>Vehicle Only</option>
        </select>
      </div>
      <p className="text-[10px] text-gray-500">Draw polygon zones. Click to add points, click near first point to close the shape.</p>
      <ZoneEditor
        cameraId={camera.id}
        authToken={authToken}
        mode="polygon"
        polygons={config.regions.map(r => ({
          id: r.id,
          points: r.points,
          color: 'rgba(168, 85, 247, 0.3)',
        }))}
        onPolygonComplete={(points: Point[]) => {
          const newRegion = { id: config.regions.length, enabled: true, points }
          setConfig({ ...config, regions: [...config.regions, newRegion] })
        }}
        onDelete={id => setConfig({ ...config, regions: config.regions.filter(r => r.id !== id) })}
      />
      <button onClick={onSave} disabled={saving} className={btnSave}>
        {saving ? 'Saving...' : `Save ${label}`}
      </button>
    </div>
  )
}
